import cloneDeep from 'lodash/cloneDeep';
// eslint-disable-next-line import/no-unresolved, import/extensions
import * as constants from './constants';

const formFieldKey = (key, description) => ({
  key,
  type: 'string',
  description,
  regex: '',
  properties: {},
});

const mergeOverlapFields = (mergeObjectFields, removeFieldArray) => {
  // remove overlaps with mergeObjectFields in req and optional
  // find fields in optional that may already be in required to remove
  const mergeOptionalLength = mergeObjectFields.elements.properties.optional[0].length;
  let index = 0;
  let tailIndex = mergeOptionalLength - 1;
  let count = 0;
  while (index <= tailIndex) {
    // if the addr req were included and found in optional, need to be removed
    if (removeFieldArray.includes(
      mergeObjectFields.elements.properties.optional[0][index].key,
    )) {
      const temp = mergeObjectFields.elements.properties.optional[0][index];
      // eslint-disable-next-line max-len
      mergeObjectFields.elements.properties.optional[0][index] = mergeObjectFields.elements.properties.optional[0][tailIndex];
      mergeObjectFields.elements.properties.optional[0][tailIndex] = temp;
      count += 1;
      tailIndex -= 1;
    } else {
      index += 1;
    }
  }
  mergeObjectFields.elements.properties.optional[0].splice(tailIndex + 1, count);
  return mergeObjectFields;
};

const createEmbedAddresses = (
  embedAddresses, representativeIndex, fieldType, applicationFields,
) => {
  const repreObject = applicationFields[fieldType][representativeIndex];
  // fetch the required and optional representatives.addresses fields
  const reprAddrReqIndex = repreObject.elements.properties.required[0].findIndex(
    f => f.key === 'addresses',
  );
  const reprAddrOptIndex = repreObject.elements.properties.optional[0].findIndex(
    f => f.key === 'addresses',
  );
  const addressesReqFields = embedAddresses.elements.properties.required[0].map(
    f => f.key,
  );
  const addressesOptFields = embedAddresses.elements.properties.optional[0].map(
    f => f.key,
  );
  let reprAddrReqFields = [];
  if (reprAddrReqIndex > -1) {
    // pull out the optional addresses from appFields
    const optRAddressObject = repreObject.elements.properties
      .required[0][reprAddrReqIndex].elements.properties.optional[0];
    const reprAddrOprFields = optRAddressObject.map(
      f => f.key,
    );

    // pull out the required addresses
    const reprAddressObject = repreObject.elements.properties
      .required[0][reprAddrReqIndex].elements.properties.required[0];
    reprAddrReqFields = reprAddressObject.map(
      f => f.key,
    );
    const diffReqFields = reprAddrReqFields.filter(
      f => !addressesReqFields.includes(f),
    );

    const diffOprFields = reprAddrOprFields.filter(
      f => !addressesOptFields.includes(f),
    );
    // find the required in default that are not in representatives
    // so makes sense to loop through
    // find what's not in the default Representatives
    reprAddressObject.forEach(f => {
      if (diffReqFields.includes(f.key)) {
        embedAddresses.elements.properties.required[0].push(f);
      }
    });
    optRAddressObject.forEach(f => {
      if (diffOprFields.includes(f.key)) {
        embedAddresses.elements.properties.optional[0].push(f);
      }
    });
  }
  let reprAddrOptFields = [];
  if (reprAddrOptIndex > -1) {
    // pull out the required addresses
    const reprAddressObject = repreObject.elements.properties
      .required[0][reprAddrOptIndex]?.elements?.properties?.optional[0] || [];
    reprAddrOptFields = reprAddressObject.map(
      f => f.key,
    );
    const diffOptFields = reprAddrOptFields.filter(
      f => !addressesOptFields.includes(f),
    );
    // find the required in default that are not in representatives
    // so makes sense to loop through
    // find what's not in the default Representatives
    reprAddressObject.forEach(f => {
      if (diffOptFields.includes(f.key)) {
        embedAddresses.elements.properties.optional[0].push(f);
      }
    });
  }
  return mergeOverlapFields(embedAddresses, reprAddrReqFields);
};

// merges business_address with form data addresses together
const createAddresses = (addresses, addressesIndex, fieldType, applicationFields) => {
  const addressReqObject = applicationFields[fieldType][addressesIndex]
    .elements.properties.required[0];
  const addressOptObject = applicationFields[fieldType][addressesIndex]
    .elements.properties.optional[0];

  const addressesReqFields = addressReqObject.map(f => f.key);
  const addressesOptFields = addressOptObject.map(f => f.key);
  const addrRequiredFields = addresses.elements.properties.required[0]
    .map(f => f.key);
  const addrOptionalFields = addresses.elements.properties.optional[0]
    .map(f => f.key);

  const extraRequired = addrRequiredFields
    .filter(value => !addressesReqFields.includes(value));
  const extraOptional = addressesOptFields
    .filter(value => !addrOptionalFields.includes(value));
  addressReqObject.forEach(f => {
    if (extraRequired.includes(f.key)) {
      addresses.elements.properties.required[0].push(f);
    }
  });
  addressOptObject.forEach(f => {
    if (extraOptional.includes(f.key)) {
      addresses.elements.properties.optional[0].push(f);
    }
  });

  return mergeOverlapFields(addresses, addrRequiredFields);
};

const createRepresentatives = (
  representatives, representativeIndex, fieldType, applicationFields,
) => {
  const defaultRepresentative = applicationFields[fieldType][representativeIndex];
  const representativeReqFields = representatives.elements.properties.required[0].map(f => f.key);

  const artificialReqFields = defaultRepresentative.elements.properties.required[0].map(f => f.key);

  // find fields that are either optional or req
  const diffReqFields = artificialReqFields.filter(
    f => !representativeReqFields.includes(f),
  );
  // find the required in default that are not in representatives
  // so makes sense to loop through
  // find what's not in the default Representatives
  defaultRepresentative.elements.properties.required[0].forEach(f => {
    if (diffReqFields.includes(f.key)) {
      representatives.elements.properties.required[0].push(f);
    }
  });

  // need to do this with optional as well
  const representativeOptFields = representatives.elements.properties.optional[0]
    .map(f => f.key);

  const artificialOptFields = defaultRepresentative.elements.properties.optional[0].map(
    f => f.key,
  );

  const diffOptFields = artificialOptFields.filter(
    f => !representativeOptFields.includes(f),
  );

  // loop through the optional
  defaultRepresentative.elements.properties.optional[0].forEach(f => {
    if (diffOptFields.includes(f.key) && !representativeReqFields.includes(f.key)) {
      representatives.elements.properties.optional[0].push(f);
    }
  });
  return mergeOverlapFields(representatives, artificialReqFields);
};

export const removeDuplicatedFormDataFields = formData => {
  if ({}.hasOwnProperty.call(formData, 'addresses')) {
    if (formData.addresses[0].type === 'business_primary') {
      constants.BUSINESS_ADDRESS_FIELDS.forEach(f => {
        if ({}.hasOwnProperty.call(formData, f)) {
          delete formData[f];
        }
      });
    }
    if (formData.addresses[0].type === 'primary') {
      constants.ADDRESS_FIELDS.forEach(f => {
        if ({}.hasOwnProperty.call(formData, f)) {
          delete formData[f];
        }
      });
    }
  }
  return formData;
};

export const formatRootLevelFields = (modApplicationFields, isBusinessType) => {
  let genderIncluded = false;
  let businessType = isBusinessType;
  let addressesIncluded = false;
  // TODO: Remove this hardcoded filter when GET /parameters gets fixed
  ['required', 'optional'].forEach(outerModField => {
    if ({}.hasOwnProperty.call(modApplicationFields, outerModField)) {
      modApplicationFields[outerModField] = modApplicationFields[outerModField]
        .filter(f => !['answers', 'verification_code'].includes(f.key));

      // add an outer array for representatives and addresses
      // used for supporting multiple addresses and representatives
      modApplicationFields[outerModField].forEach(f => {
        f.fieldType = outerModField;
        if (f.key === 'gender') genderIncluded = true;
        if (f.key === 'addresses') {
          f.elements.properties = {
            ...f.elements.properties,
            required: [f.elements.properties.required],
            optional: [f.elements.properties.optional],
          };
          addressesIncluded = true;
        }
        if (f.key === 'representatives') {
          // located addresses and make that an indexed array
          ['required', 'optional'].forEach(fieldType => {
            f.elements.properties[fieldType].forEach(inner => {
              if (inner.key === 'addresses') {
                inner.elements.properties = {
                  ...inner.elements.properties,
                  required: [inner.elements.properties.required],
                  optional: [inner.elements.properties.optional],
                };
              }
            });
          });
          f.elements.properties = {
            ...f.elements.properties,
            required: [f.elements.properties.required],
            optional: [f.elements.properties.optional],
          };
          businessType = true;
        }
      });
    }
  });

  // add an outer array for OR addresses field
  if ({}.hasOwnProperty.call(modApplicationFields, 'or')) {
    modApplicationFields.or.forEach(fieldGroup => {
      fieldGroup.required.forEach(field => {
        if (field.key === 'addresses') {
          field.elements.properties = {
            ...field.elements.properties,
            required: [field.elements.properties.required],
            optional: [field.elements.properties.optional],
          };
        } else if (field.key === 'representatives') {
          field.elements.properties = {
            ...field.elements.properties,
            required: [field.elements.properties.required],
            optional: [field.elements.properties.optional],
          };
        }
      });
    });
  }
  return [modApplicationFields, businessType, addressesIncluded, genderIncluded];
};

export const formatPersonWorkflowFields = (modApplicationFields, addressesIncluded) => {
  const addresses = {
    description: 'Addresses',
    key: 'addresses',
    elements: {
      properties: {
        required: [[formFieldKey('type', '(primary, secondary, previous)')]],
        optional: [[]],
      },
    },
    regex: '',
    type: 'array',
  };
  let filledReqAddress = false;
  let filledOptAddress = false;
  ['required', 'optional'].forEach(fieldType => {
    if ({}.hasOwnProperty.call(modApplicationFields, fieldType)) {
      modApplicationFields[fieldType].forEach(f => {
        if (constants.ADDRESS_FIELDS.includes(f.key)) {
          addresses.elements.properties[fieldType][0].push(
            formFieldKey(constants.ADDRESS_FIELDS_DICT[f.key], f.description),
          );
          if (fieldType === 'required') {
            filledReqAddress = true;
          } else {
            filledOptAddress = true;
          }
        }
      });
      modApplicationFields[fieldType] = modApplicationFields[fieldType].filter(
        f => !constants.ADDRESS_FIELDS.includes(f.key),
      );
    }
  });

  if (!addressesIncluded) {
    if (filledReqAddress) {
      modApplicationFields.required.push(addresses);
    } else if (filledOptAddress) {
      modApplicationFields.optional.push(addresses);
    }
  }
  let orAddresses = {};
  if ({}.hasOwnProperty.call(modApplicationFields, 'or')) {
    modApplicationFields.or.forEach((f, fIndex) => {
      filledReqAddress = false;
      orAddresses = cloneDeep(addresses);
      orAddresses.elements.properties.required = [[]];
      orAddresses.elements.properties.optional = [[]];
      const reqFields = f.required;
      reqFields.forEach(rF => {
        if (constants.ADDRESS_FIELDS.includes(rF.key)) {
          orAddresses.elements.properties.required[0].push(
            formFieldKey(constants.ADDRESS_FIELDS_DICT[rF.key], rF.description),
          );
          filledReqAddress = true;
        }
      });
      modApplicationFields.or[fIndex].required = modApplicationFields.or[fIndex].required.filter(
        addrField => !constants.ADDRESS_FIELDS.includes(addrField.key),
      );
      // splice if you found an address fields and yet already have addresses included elsewhere
      // 1: length of this OR field is empty
      // 2. and there's not a filledReqAddress meaning that address something won't get added
      // if there's already an addresses then filledReqAddress means nothing
      if ((!filledReqAddress || addressesIncluded)
        && modApplicationFields.or[fIndex].required.length === 0) {
        modApplicationFields.or.splice(fIndex, 1);
      }
      if (!addressesIncluded && filledReqAddress) {
        modApplicationFields.or[fIndex].required.push(orAddresses);
      }
    });
  }
  return modApplicationFields;
};

export const formatBusinessWorkflowFields = (modApplicationFields, isBusinessType) => {
  let businessType = isBusinessType;
  // construct templates for root addresses, representatives.addresses, and the repr
  let addresses = {
    description: 'Addresses',
    key: 'addresses',
    elements: {
      key: '',
      description: '',
      properties: {
        optional: [[]],
        required: [[
          formFieldKey('type', '(business_primary, business_secondary, business_previous)'),
        ]],
      },
    },
    regex: '',
    type: 'array',
  };
  let embedAddresses = {
    key: 'addresses',
    description: 'Addresses',
    elements: {
      properties: {
        required: [[formFieldKey('type', '(primary, secondary, previous)')]],
        optional: [[]],
      },
      regex: '',
    },
    type: 'array',
  };
  let representatives = {
    key: 'representatives',
    type: 'array',
    description: 'Representatives',
    elements: {
      properties: {
        required: [[formFieldKey('type', '(principal_owner, authorized_signer)')]],
        optional: [[]],
      },
    },
    regex: '',
  };
  let filledReqAddress = false;
  let filledOptAddress = false;

  ['required', 'optional'].forEach(fieldType => {
    if ({}.hasOwnProperty.call(modApplicationFields, fieldType)) {
      // checking to see if business_address_ fields are present
      // in required and if so add them to the addresses object
      modApplicationFields[fieldType].forEach(f => {
        if (f.key === 'addresses') {
          const elementAddresses = f.elements.properties[fieldType][0];
          elementAddresses.forEach(address => {
            addresses.elements.properties[fieldType][0].push(
              formFieldKey(constants.BUSINESS_ADDRESS_FIELDS_DICT[`business_address_${address.key}`], address.description),
            );
          });
          if (fieldType === 'required') {
            filledReqAddress = true;
          }
        } else if (constants.BUSINESS_ADDRESS_FIELDS.includes(f.key)) {
          addresses.elements.properties[fieldType][0].push(
            formFieldKey(constants.BUSINESS_ADDRESS_FIELDS_DICT[f.key], f.description),
          );
          if (fieldType === 'required') {
            filledReqAddress = true;
          } else {
            filledOptAddress = true;
          }
          businessType = true;
        }
        // check if any of the representative fields are present
        // only do this for business type workflow though
        if (constants.REPRESENTATIVE_FIELDS.includes(f.key)) {
          representatives.elements.properties[fieldType][0].push(
            formFieldKey(f.key, f.description),
          );
        }
        if (constants.ADDRESS_FIELDS.includes(f.key)) {
          embedAddresses.elements.properties[fieldType][0].push(
            formFieldKey(constants.ADDRESS_FIELDS_DICT[f.key], f.description),
          );
        }
      });
      // remove any fields that were pulled from these addresses in required
      modApplicationFields[fieldType] = modApplicationFields[fieldType].filter(
        f => !Array.prototype.concat(
          constants.BUSINESS_ADDRESS_FIELDS,
          constants.REPRESENTATIVE_FIELDS,
          constants.ADDRESS_FIELDS,
        )
          .includes(f.key),
      );
    }
  });

  // contains embedded addresses that need to be merged
  // if optionalAddresses are greater than 0 or required is greater than one
  // one is due to the fact we add the type element by default
  if (embedAddresses.elements.properties.optional[0].length > 0
    || embedAddresses.elements.properties.required[0].length > 1) {
    const representativeIndex = modApplicationFields.required.findIndex(
      f => f.key === 'representatives',
    );
    const representativeOptIndex = modApplicationFields.optional.findIndex(
      f => f.key === 'representatives',
    );

    if (representativeIndex > -1) {
      embedAddresses = createEmbedAddresses(
        embedAddresses, representativeIndex, 'required', modApplicationFields,
      );
    } else if (representativeOptIndex > -1) {
      embedAddresses = createEmbedAddresses(
        embedAddresses, representativeOptIndex, 'optional', modApplicationFields,
      );
    }
    // embedded addresses is a required field for representatives
    representatives.elements.properties.required[0].push(embedAddresses);
    if (representatives.elements.properties.required[0].length > 1
      || (representatives.elements.properties.optional[0].length > 0)) {
      // representativeIndex already exists in required and needs to be merged
      if (representativeIndex > -1) {
        representatives = createRepresentatives(
          representatives, representativeIndex, 'required', modApplicationFields,
        );
        modApplicationFields.required[representativeIndex] = representatives;
      } else if (representativeOptIndex > -1) {
        representatives = createRepresentatives(
          representatives, representativeOptIndex, 'optional', modApplicationFields,
        );
        modApplicationFields.optional[representativeOptIndex] = representatives;
      } else {
        modApplicationFields.required.push(representatives);
      }
    }
  }

  // check if addresses exist in formData and merge the fields together
  const addressesIndex = modApplicationFields.required.findIndex(
    f => f.key === 'addresses',
  );
  const addressesOptionalIndex = modApplicationFields.optional.findIndex(
    f => f.key === 'addresses',
  );
  // contained transformed business_address required fields
  if (filledReqAddress) {
    // required field path and can use that index
    if (addressesIndex > -1) {
      addresses = createAddresses(
        addresses, addressesIndex, 'required', modApplicationFields,
      );
      modApplicationFields.required[addressesIndex] = addresses;
    } else if (addressesOptionalIndex > -1) {
      addresses = createAddresses(
        addresses, addressesOptionalIndex, 'optional', modApplicationFields,
      );
      // need to delete the modApplication.optional since has at least 1 required field
      modApplicationFields.optional.splice(addressesOptionalIndex, 1);
      // pushes addresses into required key
      modApplicationFields.required.push(addresses);
    } else { // addresses do not currently exist in the original form data
      modApplicationFields.required.push(addresses);
    }
  } else if (filledOptAddress) { // has an optional field
    if (addressesIndex > -1) {
      addresses = createAddresses(
        addresses, addressesIndex, 'required', modApplicationFields,
      );
      modApplicationFields.required[addressesIndex] = addresses;
    } else if (addressesOptionalIndex > -1) {
      addresses = createAddresses(
        addresses, addressesOptionalIndex, 'optional', modApplicationFields,
      );
      modApplicationFields.optional[addressesIndex] = addresses;
    } else { // addresses do not currently exist in the original form data
      modApplicationFields.optional.push(addresses);
    }
  }
  return [modApplicationFields, businessType];
};

export const formatTmxLocalAttributesAndBankAccounts = modApplicationFields => ({
  ...modApplicationFields,
  optional: modApplicationFields.optional?.map(field => {
    if (field.key === 'tmx_local_attributes' || field.key === 'bank_accounts') {
      const modField = cloneDeep(field);
      const requiredOfModField = modField.elements.properties.required;
      const optionalOfModField = modField.elements.properties.optional;
      modField.elements.properties.required = [requiredOfModField];
      modField.elements.properties.optional = [optionalOfModField];
      return modField;
    } return field;
  }),
});
